import { cn } from "@/lib/utils";

/**
 * Cinch logo. The wordmark and mono mark inherit color from CSS `currentColor`,
 * but `<img src=*.svg>` does NOT pipe color through. So we inline the SVG paths
 * here. To keep this file from owning the artwork, the paths come from a
 * generated module — see scripts/sync-logo-paths.ts (not yet wired). For now
 * the paths are inline.
 *
 * Cinch brand rule: NEVER render the wordmark or wave in black. Only brand cyan
 * (`tone="brand"`) or white (`tone="white"`). This component enforces it by
 * exposing only those two tones — no arbitrary className override of `text-*`.
 */

type LogoTone = "brand" | "white";
type LogoVariant = "wordmark" | "mark" | "mark-mono";

interface LogoProps {
  variant?: LogoVariant;
  tone?: LogoTone;
  className?: string;
  title?: string;
}

const TONE_CLASS: Record<LogoTone, string> = {
  brand: "text-brand",
  white: "text-white",
};

export function Logo({
  variant = "wordmark",
  tone = "brand",
  className,
  title = "Cinch",
}: LogoProps) {
  if (variant === "mark") return <MarkGradient className={className} title={title} />;
  const toneClass = TONE_CLASS[tone];
  if (variant === "mark-mono") return <MarkMono className={cn(toneClass, className)} title={title} />;
  return <Wordmark className={cn(toneClass, className)} title={title} />;
}

function Wordmark({ className, title }: { className?: string; title: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1014 342.8"
      role="img"
      aria-label={title}
      className={className}
    >
      <g fill="currentColor">
        <path d="M183.5,233.5c-12.6,17.1-59.2-8.2-64.6-24.8,26-12.3,17.5-34.3,5.3-35.3-16.5-1.5-40.3,25.9-43.7,68.5-2.9,36.7,20.4,54.3,50.5,52.8,29.6-1.9,61.2-16.1,82.1-58.8,2.9-6.9,23.3,7.4,20.9,11.3-39.3,75.9-92.7,93.9-146,92C36.4,337.3-1,297.2,0,246.3c1.5-64.1,54.4-119.9,132.1-118.9,70.4,1,89.3,56.5,51.5,106" />
        <path d="M203.4,274.8c5.8-24.8,12.2-49.5,19.2-74.2,6.3-22.4,6.8-70.1,34.3-74.5,6.3-1,65.6.5,61.5,14.7-16,56.2-31.1,100.2-35.9,126.8-2.9,18.7-.5,28.4,5.8,28.4,17,.5,46.1-23,62.6-58.8,2.9-6.9,23.3,7.4,20.9,11.8-22.3,42.1-59.7,89.1-115.1,91.5-51.9,2.4-62.6-25.9-53.4-65.6v-.2ZM260.7,60.5c4.9-23,23.3-41.1,49-40.6,25.2.5,38.4,18.5,34.5,40.6-3.9,22.4-21.8,40.6-50.5,40.6s-37.4-19-33-40.6Z" />
        <path d="M543.6,192.5c-4.9,20-15.5,49.3-20.4,71.9-3.9,18.2-1.5,31.3,7.3,31.3,18,0,42.2-27.4,57.3-58.8,3.4-6.9,23.3,6.9,20.9,11.8-19.4,37.2-57.3,90.4-112.2,92-50,1.5-61.2-26.4-51.5-65.1,3.9-15.2,16-65.1,18-73.8,2.9-13.2,2.9-21.6-10.7-19.5-10.2,1.5-21.8,8.7-34.5,19-14.1,45.9-22.3,86.2-33.5,131-8.2,11.8-72.3,14.2-78.2-2.9,12.9-57.2,29.4-113.6,46.4-169.7,2.9-9.8,4.4-25.4,12.9-31,5.8-3.8,16.7-3.6,23.3-3.8,4.4,0,49.3,4.3,45.4,14.6-2.9,7.9-4.9,16.6-7.8,24.5,17.5-13.7,48.6-37.2,77.7-36.7,37.9.5,48.6,26.4,39.3,65.1" />
        <path d="M747.6,234.5c-12.6,17.1-59.2-8.2-64.6-24.8,26-12.3,17.5-34.3,5.3-35.3-16.5-1.5-40.3,25.9-43.7,68.5-2.9,36.7,20.4,54.3,50.5,52.8,29.6-1.9,61.2-16.1,82.1-58.8,2.9-6.9,23.3,7.4,20.9,11.3-39.3,75.9-92.7,93.9-146,92-51.5-1.9-88.9-42.1-87.9-92.8,1.5-64.1,54.4-119.9,132.1-118.9,70.4,1,89.3,56.5,51.5,106" />
        <path d="M920.3,17.5c-9.2,27.9-26.7,84.1-41.8,142.3,18-13.7,47.6-32.2,74.8-32.2,42.7,0,52,26.9,40.3,65.1-9.7,31.9-40.8,93.7-15.3,102.9,8.5,3.1,15.3,3.6,28.2-7.7,4.1-3.8,9.4-.5,6.5,4.3-13.9,30.7-37.7,48.1-69.4,48.5-49.6.5-57.3-25.4-53.4-54.8,3.4-25,19.4-75.4,22.3-84.1,4.4-12.7,3.4-22.4-10.2-21.1-9.2,1-23.8,9.2-35.9,20.6-13.1,49.3-24.8,93.3-34,131.5-2.4,10.8-86.4,17.1-80.6-5,27.2-103.1,68-244.9,91.3-322.4,17-11.6,78.2-3.4,77.2,11.8" />
      </g>
    </svg>
  );
}

function MarkMono({ className, title }: { className?: string; title: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 765 651"
      role="img"
      aria-label={title}
      className={className}
    >
      <g fill="currentColor">
        <path d="M503,333.3c5.2-12.3-5.7-15.8-11.7-4.9c-22.6,45.7-56.7,60.9-88.8,63c-32.6,1.6-57.8-17.3-54.6-56.7c3.7-45.7,29.4-75.1,47.3-73.5c13.1,1.1,22.4,24.6-5.8,37.8c5.8,17.9,56.2,45,69.9,26.6c41-53.1,20.5-112.7-55.7-113.8c-0.7,0-1.5,0-2.2,0c-82.9,0-139.1,59.4-140.7,127.7c-1.1,54.6,39.4,97.7,95.1,99.8C413.5,441.3,460.4,414.7,503,333.3z" />
        <path d="M420.3,404.7c23.3-9.7,42.2-28.7,56.6-53c0,0-25.2,40.4-84.1,39.7c0,0-40.4-0.2-44.9-43.8c-2.2-21.4,4.3-45.1,15.7-63.4c7.4-11.9,23.1-29.9,38.3-19.7c0,0,12.1,8.1,1.3,24.2c0,0-7.7,9.3-13.8,10.3c0,0,29-1.8,29.7-31.3c0.7-29.5-34.1-42.8-34.1-42.8s-8.3-3.9-21.6-3.9c-10.3,0-23.6,2.3-38.4,10.6c-32.3,18-57.8,53.6-63.2,90c-2.6,17.9,0.9,30.1,10.2,45.3c12.8,20.9,34.1,37.6,58.3,42.7C360.3,415.9,391.5,416.7,420.3,404.7z" />
      </g>
    </svg>
  );
}

function MarkGradient({ className, title }: { className?: string; title: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 765 651"
      role="img"
      aria-label={title}
      className={className}
    >
      <defs>
        <radialGradient id="cinch-mark-wave" cx="301.9177" cy="329.004" r="224.2251" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#B4E2EE" />
          <stop offset="0.1659" stopColor="#AEDEED" />
          <stop offset="0.3864" stopColor="#9DD4E9" />
          <stop offset="0.6376" stopColor="#82C3E4" />
          <stop offset="0.9096" stopColor="#5BABDC" />
          <stop offset="1" stopColor="#4DA2D9" />
        </radialGradient>
        <linearGradient id="cinch-mark-curl" gradientUnits="userSpaceOnUse" x1="335.2855" y1="317.4602" x2="456.1813" y2="317.4602">
          <stop offset="0" stopColor="#3D96D2" />
          <stop offset="0.1993" stopColor="#388FCA" />
          <stop offset="0.5131" stopColor="#2A7DB3" />
          <stop offset="0.9" stopColor="#145F8F" />
          <stop offset="1" stopColor="#0D5684" />
        </linearGradient>
      </defs>
      <path fill="url(#cinch-mark-wave)" d="M503,333.3c5.2-12.3-5.7-15.8-11.7-4.9c-22.6,45.7-56.7,60.9-88.8,63c-32.6,1.6-57.8-17.3-54.6-56.7c3.7-45.7,29.4-75.1,47.3-73.5c13.1,1.1,22.4,24.6-5.8,37.8c5.8,17.9,56.2,45,69.9,26.6c41-53.1,20.5-112.7-55.7-113.8c-0.7,0-1.5,0-2.2,0c-82.9,0-139.1,59.4-140.7,127.7c-1.1,54.6,39.4,97.7,95.1,99.8C413.5,441.3,460.4,414.7,503,333.3z" />
      <path fill="url(#cinch-mark-curl)" d="M420.3,404.7c23.3-9.7,42.2-28.7,56.6-53c0,0-25.2,40.4-84.1,39.7c0,0-40.4-0.2-44.9-43.8c-2.2-21.4,4.3-45.1,15.7-63.4c7.4-11.9,23.1-29.9,38.3-19.7c0,0,12.1,8.1,1.3,24.2c0,0-7.7,9.3-13.8,10.3c0,0,29-1.8,29.7-31.3c0.7-29.5-34.1-42.8-34.1-42.8s-8.3-3.9-21.6-3.9c-10.3,0-23.6,2.3-38.4,10.6c-32.3,18-57.8,53.6-63.2,90c-2.6,17.9,0.9,30.1,10.2,45.3c12.8,20.9,34.1,37.6,58.3,42.7C360.3,415.9,391.5,416.7,420.3,404.7z" />
    </svg>
  );
}
